import { useState, useLayoutEffect, useEffect } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';

/**
 * Ancestor marker the navigation drawer (ws-navigation-drawer) puts on the
 * section wrapping this fragment. Cross-repo contract — see that repo's README
 * ("The data-rec-vehicles-context contract").
 */
export const NAV_DRAWER_CONTEXT_SELECTOR =
	'[data-rec-vehicles-context="nav-drawer"]';

// Resolve before paint on the client so the first painted layout is already
// correct; fall back to a plain effect on the server to avoid the
// useLayoutEffect SSR warning. Detection is inherently client-only anyway — the
// drawer context exists only in the browser DOM the fragment is injected into.
const useIsomorphicLayoutEffect = isBrowser ? useLayoutEffect : useEffect;

/**
 * Detects whether this widget instance is hydrating inside the navigation
 * drawer by probing its container's ancestry for the drawer's context marker.
 *
 * Detection is per-instance (scoped by DOM containment via closest), so a page
 * hosting both a drawer instance and an in-page instance resolves each one
 * correctly.
 *
 * Defaults to `false` — never an "undetermined" value — so the server and the
 * client's first render agree: the server can never be inside a drawer, so the
 * non-drawer markup (including the mobile carousel) still server-renders and
 * hydrates without a mismatch. The client probe then upgrades to `true` before
 * paint (via the layout effect) only when the marker is found, flipping that
 * one instance to the drawer layout.
 *
 * @param {React.RefObject<HTMLElement>} containerRef - ref on the widget's root
 * @returns {boolean} true inside the drawer, false otherwise
 */
const useNavDrawerContext = (containerRef) => {
	const [isNavDrawer, setIsNavDrawer] = useState(false);

	// containerRef is a stable useRef object, so this runs exactly once on mount;
	// the probe reads containerRef.current (populated by commit time), not the
	// ref object itself.
	useIsomorphicLayoutEffect(() => {
		if (!isBrowser) return;
		setIsNavDrawer(
			Boolean(containerRef.current?.closest(NAV_DRAWER_CONTEXT_SELECTOR))
		);
	}, [containerRef]);

	return isNavDrawer;
};

export default useNavDrawerContext;
